import { apiService } from './api';
import {
  BillingSummaryDto,
  BillingProfileDto,
  UpdateBillingProfileInput,
  CreditPackDto,
  CreditOrderDto,
  CreateOrderResultDto,
  ProductOfferDto,
  SubscriptionStatusDto,
} from '../types/portalBilling';

/**
 * Client for the External-portal billing read surface (`/api/portal/billing`, served by
 * `PortalBillingController`, Phase 1b). Credits are per account (plan P1); the summary carries the
 * `enabled` mirror of the `Billing:Enabled` kill switch (plan P15).
 *
 * Mirrors `portalProfileService`: every method returns the raw `ApiResponse<T>` envelope so callers
 * branch on `success`/`message`/`errors` (a 400 on profile save carries the validation bullets).
 */
export const portalBillingService = {
  /** Balance, free-analysis flag and the checkout-readiness checklist. Grants the sign-up bonus lazily. */
  getSummary: (signal?: AbortSignal) =>
    apiService.get<BillingSummaryDto>('/portal/billing/summary', undefined, signal),

  /** The caller's invoice identity (Individual default when no row exists yet). */
  getProfile: (signal?: AbortSignal) =>
    apiService.get<BillingProfileDto>('/portal/billing/profile', undefined, signal),

  /** Upsert the invoice identity. Validation failures come back as a failure envelope (400). */
  updateProfile: (body: UpdateBillingProfileInput) =>
    apiService.put<BillingProfileDto>('/portal/billing/profile', body),

  /** Active packs with VAT-inclusive totals, ordered by credits. */
  getPacks: (signal?: AbortSignal) =>
    apiService.get<CreditPackDto[]>('/portal/billing/packs', undefined, signal),

  /**
   * The checkout offer for ONE product by code (the subscription-product variant of the packs picker),
   * including the ZAR-presentment preview. A 404 (unknown/inactive) rejects.
   */
  getProduct: (code: string, signal?: AbortSignal) =>
    apiService.get<ProductOfferDto>(`/portal/billing/products/${encodeURIComponent(code)}`, undefined, signal),

  /** The caller's Smart Sampling subscription status (Profile › Billing card). `data` is null when never subscribed. */
  getSubscription: (signal?: AbortSignal) =>
    apiService.get<SubscriptionStatusDto | null>('/portal/billing/subscription', undefined, signal),

  /**
   * The caller's purchases, newest first — the Profile › Billing receipts table (Phase 1d endpoint).
   * Until 1d lands the route 404s; callers treat a rejected/failed load as "No receipts yet".
   */
  listOrders: (signal?: AbortSignal) =>
    apiService.get<CreditOrderDto[]>('/portal/billing/orders', undefined, signal),

  /**
   * Create (or reuse the caller's live Pending) order for a pack and get the gateway redirect (Phase 1d).
   * Non-2xx outcomes REJECT with a `NormalizedApiError` carrying `errorCode`: 409 `BILLING_DISABLED` /
   * `BILLING_PROFILE_INCOMPLETE`, 404 `PACK_NOT_FOUND`. Only ever called from the Pay click (plan §4).
   */
  createOrder: (packId: number) =>
    apiService.post<CreateOrderResultDto>('/portal/billing/orders', { packId }),

  /** Status poll after the gateway return. A 404 (another user's / unknown order) rejects. */
  getOrder: (orderId: number, signal?: AbortSignal) =>
    apiService.get<CreditOrderDto>(`/portal/billing/orders/${orderId}`, undefined, signal),

  /**
   * Server-side verify + settle. Idempotent (credits ledgered once, CAS on the order) — safe to call from a
   * duplicate tab. 409 `PAYMENT_AMOUNT_MISMATCH` rejects with the errorCode (nothing credited).
   */
  confirmOrder: (orderId: number) =>
    apiService.post<CreditOrderDto>(`/portal/billing/orders/${orderId}/confirm`),
};
